import type { Movie, Showtime, WeekUsage } from "./types";
import type { LocationPrefs } from "./prefs";

// Planning a showtime, from the chip that shows it.
//
// The chip is the affordance because Streamlit could not make it one: a
// `st.button` cannot live inside a markdown string, so the old page drew a
// second row of buttons underneath the links and hoped the columns lined up.
// Here the plan control is part of the chip, and the state it draws itself
// from -- `planned`, `week_key` -- rides on the showtime the server already
// sent.

/** The body of a plan or unplan request.
 *
 *  Four identity fields, the format, and the location the screening was
 *  BROWSED in. The ticket URL, the runtime and the Fandango URL an auditorium
 *  is resolved from are read off the screening the SERVER re-finds -- see
 *  `server/services/planner_api.py`. Sending them from here would put scraper
 *  field names in the client and would let a caller store a ticket URL of its
 *  choosing against a real showtime.
 *
 *  The location is load-bearing for exactly that reason: `/api/planner/plan`
 *  re-finds the screening in `get_showtimes(date, resolve_theaters(...))`, and
 *  an absent city resolves to the DEFAULT one, so a Boston screening was
 *  looked for in San Diego's listings and every plan made outside San Diego
 *  answered 404. It is a required argument rather than an optional one because
 *  leaving it out is invisible at the call site -- the request still reads as
 *  complete, and the four identity fields are still all correct.
 *
 *  Unplan ignores the extra keys, the way it already ignores `format`: it
 *  re-finds nothing, which is also why it never had this bug.
 */
export function planRequest(theater: string, showtime: Showtime, location: LocationPrefs) {
  return {
    date: showtime.date,
    time: showtime.time,
    theater,
    movie_title: showtime.movie_title,
    format: showtime.format,
    ...(showtime.screening_id ? { screening_id: showtime.screening_id } : {}),
    ...(location.cityId ? { city: location.cityId } : {}),
    ...(location.theaterIds.length > 0 ? { theaters: location.theaterIds } : {}),
  };
}

/** Whether a screening's week has no A-List allowance left.
 *
 *  Unknown weeks are NOT full. A week the server did not report on (an
 *  unparseable date, or a response built with no planner behind it) must leave
 *  the control usable: refusing the write would be a block with no explanation
 *  the user could act on, and the server enforces the limit anyway.
 */
export function weekIsFull(weeks: Record<string, WeekUsage>, showtime: Showtime): boolean {
  return weeks[showtime.week_key]?.full ?? false;
}

// In-flight plan/unplan writes fired from the Finder's chip, tracked so the
// Planner page's initial load can wait for one that raced its own mount.
//
// The chip flips optimistically and its write is fire-and-forget (`onClick`
// in `MovieCard.tsx` does not await it), so clicking "Add to planner" and
// immediately switching to the Planner page can have its GET land before the
// Sheets write it is racing. `Planner.tsx` fetches once on mount and never
// polls again, so without this the new plan is missing until a full manual
// reload rather than a moment later on its own.
const pendingWrites = new Set<Promise<unknown>>();

/** Register a plan/unplan write so `waitForPendingPlannerWrites` can await it.
 *  Returns the same promise, unmodified, so the caller's own error handling
 *  is unaffected.
 */
export function trackPlannerWrite<T>(promise: Promise<T>): Promise<T> {
  const settled = promise.catch(() => undefined);
  pendingWrites.add(settled);
  void settled.finally(() => pendingWrites.delete(settled));
  return promise;
}

/** Resolves once every write tracked so far has settled. A snapshot, not a
 *  subscription -- a write started after this is called is not waited on.
 */
export function waitForPendingPlannerWrites(): Promise<void> {
  return Promise.all(pendingWrites).then(() => undefined);
}

function sameScreening(a: Showtime, b: Showtime): boolean {
  return (
    a.date === b.date &&
    a.time === b.time &&
    a.movie_title === b.movie_title &&
    a.format === b.format &&
    a.auditorium === b.auditorium &&
    a.screening_id === b.screening_id
  );
}

/** A copy of `movies` with one screening's `planned` flag flipped.
 *
 *  The theater is matched too, because two theaters can run the same film at
 *  the same minute and only one of them was clicked.
 */
export function applyPlannedToMovies(movies: Movie[], theater: string, target: Showtime, planned: boolean): Movie[] {
  const mapShowtimes = (showtimes: Showtime[], theaterName: string) =>
    theaterName === theater
      ? showtimes.map((s) => (sameScreening(s, target) ? { ...s, planned } : s))
      : showtimes;

  return movies.map((movie) => ({
    ...movie,
    theaters: movie.theaters?.map((t) => ({
      ...t,
      showtimes: mapShowtimes(t.showtimes, t.theater),
    })),
    dates: movie.dates?.map((group) => ({
      ...group,
      theaters: group.theaters.map((t) => ({
        ...t,
        showtimes: mapShowtimes(t.showtimes, t.theater),
      })),
    })),
  }));
}

/** The week block with one week's usage moved by a single plan.
 *
 *  Clamped at zero on the way down. A week can be over its limit legitimately
 *  -- four films seen and one planned before the guard existed -- and an
 *  unplan that drove the count negative would then report a free slot that is
 *  not there.
 */
export function applyPlannedToWeeks(
  weeks: Record<string, WeekUsage>,
  weekKey: string,
  delta: number,
): Record<string, WeekUsage> {
  const week = weeks[weekKey];
  if (!week) return weeks;
  const used = Math.max(0, week.used + delta);
  return { ...weeks, [weekKey]: { ...week, used, full: used >= week.limit } };
}

/** The response as it looks once a plan has landed.
 *
 *  Applied optimistically and rolled back by re-fetching on failure, so it has
 *  to move BOTH halves: the chip reads `planned`, and every other chip in the
 *  same week reads `weeks` to decide whether it is still offerable. Updating
 *  only the first leaves four planned films in a week that still says 3/4.
 */
export function applyPlanned<T extends { movies: Movie[]; weeks: Record<string, WeekUsage> }>(
  body: T,
  theater: string,
  showtime: Showtime,
  planned: boolean,
): T {
  return {
    ...body,
    movies: applyPlannedToMovies(body.movies, theater, showtime, planned),
    weeks: applyPlannedToWeeks(body.weeks, showtime.week_key, planned ? 1 : -1),
  };
}
